import { http, toApiError } from './http';

/**
 * Прямая загрузка видео урока на видеохостинг (ТЗ 17.13.3, шаг 2): файл уходит браузером по
 * `upload_url` из `POST /admin/videos/uploads`, не через наш сервер. Тот же axios-инстанс в режиме
 * `external` — без baseURL и без заголовков авторизации (токен админки третьей стороне не уходит).
 *
 * - `tus` (Kinescope): `PATCH` блоками с `Upload-Offset`, `Tus-Resumable: 1.0.0`,
 *   `Content-Type: application/offset+octet-stream`; новый сдвиг — из ответа `Upload-Offset`.
 * - `put` (Mux): `PUT` блоками с `Content-Range: bytes a-b/total` (докачка UpChunk); промежуточные
 *   блоки — `308 Resume Incomplete`.
 */
export const VIDEO_CHUNK_BYTES = 32 * 1024 * 1024;
const TUS_VERSION = '1.0.0';
/** Блок 32 МБ при медленном канале — до 30 минут. */
const CHUNK_TIMEOUT_MS = 30 * 60_000;

export interface DirectUploadTicket {
  upload_url: string;
  /** `tus` (Kinescope) / `put` (Mux). */
  protocol: string;
  headers?: Readonly<Record<string, string>> | null;
}

export interface DirectUploadOptions {
  /** Доля загруженного, 0…1. */
  onProgress?: (fraction: number) => void;
  signal?: AbortSignal;
}

/** Протокол загрузки, которого клиент не знает, — ошибка до отправки файла. */
export class UnsupportedUploadProtocolError extends Error {
  constructor(readonly protocol: string) {
    super(`video upload: unsupported protocol ${protocol}`);
    this.name = 'UnsupportedUploadProtocolError';
  }
}

function headerNumber(value: unknown): number | null {
  const number = typeof value === 'string' ? Number(value) : NaN;
  return Number.isFinite(number) ? number : null;
}

async function sendChunk(
  ticket: DirectUploadTicket,
  file: Blob,
  offset: number,
  options: DirectUploadOptions,
): Promise<number> {
  const end = Math.min(file.size, offset + VIDEO_CHUNK_BYTES);
  const chunk = file.slice(offset, end);
  const tus = ticket.protocol === 'tus';
  const headers: Record<string, string> = { ...(ticket.headers ?? {}) };
  if (tus) {
    headers['Tus-Resumable'] = TUS_VERSION;
    headers['Upload-Offset'] = String(offset);
    headers['Content-Type'] = 'application/offset+octet-stream';
  } else {
    headers['Content-Range'] = `bytes ${offset}-${end - 1}/${file.size}`;
  }
  try {
    const response = await http.request({
      url: ticket.upload_url,
      method: tus ? 'PATCH' : 'PUT',
      headers,
      data: chunk,
      external: true,
      signal: options.signal,
      timeout: CHUNK_TIMEOUT_MS,
      validateStatus: (status) => (status >= 200 && status < 300) || status === 308,
      onUploadProgress: options.onProgress
        ? (event) => {
            if (file.size > 0)
              options.onProgress?.(Math.min(1, (offset + event.loaded) / file.size));
          }
        : undefined,
    });
    // tus сообщает принятый сдвиг; без заголовка (CORS не отдал) — принят весь блок.
    return (tus ? headerNumber(response.headers['upload-offset']) : null) ?? end;
  } catch (error) {
    throw toApiError(error);
  }
}

/** Загрузить файл по протоколу видеохостинга; прогресс — по отправленным байтам. */
export async function uploadVideoFile(
  ticket: DirectUploadTicket,
  file: Blob,
  options: DirectUploadOptions = {},
): Promise<void> {
  if (ticket.protocol !== 'tus' && ticket.protocol !== 'put') {
    throw new UnsupportedUploadProtocolError(ticket.protocol);
  }
  let offset = 0;
  while (offset < file.size) {
    const next = await sendChunk(ticket, file, offset, options);
    // Сервер не продвинул сдвиг — повторять тот же блок бессмысленно.
    if (next <= offset) throw new Error('video upload: offset did not advance');
    offset = next;
  }
  options.onProgress?.(1);
}
